import React from 'react';
import { Skeleton } from '../atoms/Skeleton';

export type PageSkeletonVariant = 'list' | 'detail' | 'dashboard' | 'form';
type FormLayout = 'responsive' | 'stacked';

export interface PageSkeletonFrameProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

export interface PageHeaderSkeletonProps {
  withActions?: boolean;
  className?: string;
}

export interface SummaryStripSkeletonProps {
  count?: number;
  className?: string;
}

export interface ListSkeletonProps {
  count?: number;
  presentation?: 'rows' | 'cards';
  className?: string;
}

export interface ContentBlockSkeletonProps {
  variant?: 'detail' | 'form';
  lines?: number;
  formLayout?: FormLayout;
  withFormAction?: boolean;
  className?: string;
}

export interface PageSkeletonProps {
  label: string;
  variant: PageSkeletonVariant;
  itemCount?: number;
  formLayout?: FormLayout;
  withFormAction?: boolean;
  className?: string;
}

function line(width: string, className = 'h-3') {
  return <Skeleton width={width} className={className} />;
}

function stageStyle(index: number) {
  return { '--skeleton-stage-delay': `${index * 60}ms` } as React.CSSProperties;
}

function itemStyle(index: number) {
  return { '--skeleton-item-delay': `${Math.min(index, 6) * 25}ms` } as React.CSSProperties;
}

function SkeletonStage({ index, children, className = '' }: { index: number; children: React.ReactNode; className?: string }) {
  return (
    <div
      data-skeleton-stage={index}
      data-skeleton-motion="stage"
      className={`skeleton-stage-enter motion-reduce:animate-none ${className}`}
      style={stageStyle(index)}
    >
      {children}
    </div>
  );
}

export const PageSkeletonFrame: React.FC<PageSkeletonFrameProps> = ({ label, children, className = '' }) => (
  <section
    role="status"
    aria-label={label}
    aria-busy="true"
    className={`w-full ${className}`}
  >
    <div aria-hidden="true">{children}</div>
  </section>
);

export const PageHeaderSkeleton: React.FC<PageHeaderSkeletonProps> = ({ withActions = true, className = '' }) => (
  <header
    aria-hidden="true"
    data-skeleton-part="header"
    className={`border-b border-slate-800 pb-5 sm:pb-6 ${className}`}
  >
    <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0 flex-1 space-y-3">
        {line('7rem', 'h-2')}
        {line('min(26rem, 82%)', 'h-8 sm:h-9')}
        {line('min(38rem, 94%)')}
      </div>
      {withActions && (
        <div className="flex shrink-0 gap-2">
          <Skeleton variant="rectangular" className="h-9 w-24" />
          <Skeleton variant="rectangular" className="h-9 w-32" />
        </div>
      )}
    </div>
  </header>
);

export const SummaryStripSkeleton: React.FC<SummaryStripSkeletonProps> = ({ count = 4, className = '' }) => {
  const columns = count === 2
    ? 'sm:grid-cols-2'
    : count === 3
      ? 'sm:grid-cols-3'
      : 'sm:grid-cols-2 xl:grid-cols-4';

  return (
    <div
      aria-hidden="true"
      data-skeleton-part="summary"
      className={`grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-slate-800 bg-slate-800 ${columns} ${className}`}
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          data-skeleton-item="metric"
          data-skeleton-motion="item"
          className="skeleton-item-enter space-y-3 bg-slate-950/70 p-4 motion-reduce:animate-none"
          style={itemStyle(index)}
        >
          {line('4.5rem', 'h-2')}
          {line(index % 2 === 0 ? '5.5rem' : '7rem', 'h-6')}
        </div>
      ))}
    </div>
  );
};

function SkeletonRow({ index }: { index: number }) {
  return (
    <div
      data-skeleton-item="row"
      data-skeleton-motion="item"
      className="skeleton-item-enter grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-3 px-4 py-4 motion-reduce:animate-none sm:grid-cols-[2.5rem_minmax(0,1fr)_8rem] sm:gap-4"
      style={itemStyle(index)}
    >
      <Skeleton variant="circular" className="h-10 w-10" />
      <div className="min-w-0 space-y-2">
        {line(index % 2 === 0 ? 'min(22rem, 76%)' : 'min(18rem, 68%)', 'h-3.5')}
        {line(index % 2 === 0 ? 'min(32rem, 92%)' : 'min(27rem, 84%)', 'h-2.5')}
      </div>
      <div className="hidden justify-self-end sm:block">{line('6rem', 'h-5')}</div>
    </div>
  );
}

function SkeletonCard({ index }: { index: number }) {
  return (
    <div
      data-skeleton-item="card"
      data-skeleton-motion="item"
      className="skeleton-item-enter border border-slate-800 bg-slate-900/30 p-5 motion-reduce:animate-none"
      style={itemStyle(index)}
    >
      <div className="mb-7 flex items-start justify-between gap-4">
        <Skeleton variant="rectangular" className="h-10 w-10" />
        {line(index % 2 === 0 ? '4rem' : '5rem', 'h-5')}
      </div>
      <div className="space-y-3">
        {line(index % 2 === 0 ? '72%' : '64%', 'h-4')}
        {line('94%')}
        {line('78%')}
      </div>
    </div>
  );
}

export const ListSkeleton: React.FC<ListSkeletonProps> = ({ count = 5, presentation = 'rows', className = '' }) => {
  const items = Array.from({ length: count }, (_, index) => (
    presentation === 'rows' ? <SkeletonRow key={index} index={index} /> : <SkeletonCard key={index} index={index} />
  ));

  return presentation === 'rows' ? (
    <div
      aria-hidden="true"
      data-skeleton-part="list"
      className={`divide-y divide-slate-800 overflow-hidden rounded-lg border border-slate-800 bg-slate-900/30 ${className}`}
    >
      {items}
    </div>
  ) : (
    <div
      aria-hidden="true"
      data-skeleton-part="cards"
      className={`grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3 ${className}`}
    >
      {items}
    </div>
  );
};

export const ContentBlockSkeleton: React.FC<ContentBlockSkeletonProps> = ({
  variant = 'detail',
  lines = 4,
  formLayout = 'responsive',
  withFormAction = false,
  className = '',
}) => {
  const formGrid = formLayout === 'stacked' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2';

  return (
    <div
      aria-hidden="true"
      data-skeleton-part={variant === 'form' ? 'form' : 'content'}
      data-skeleton-form-layout={variant === 'form' ? formLayout : undefined}
      className={`border border-slate-800 bg-slate-900/30 p-5 sm:p-6 ${className}`}
    >
      <div className="mb-6 flex items-center justify-between gap-5 border-b border-slate-800 pb-4">
        <div className="flex-1 space-y-2">
          {line('min(15rem, 72%)', 'h-4')}
          {line('min(23rem, 92%)', 'h-2.5')}
        </div>
        {variant === 'detail' && <Skeleton variant="rectangular" className="h-8 w-20" />}
      </div>

      {variant === 'form' ? (
        <div className={`grid gap-5 ${formGrid}`}>
          {Array.from({ length: lines }, (_, index) => (
            <div
              key={index}
              data-skeleton-item="field"
              data-skeleton-motion="item"
              className={`skeleton-item-enter motion-reduce:animate-none ${formLayout === 'responsive' && index === lines - 1 ? 'sm:col-span-2' : ''}`}
              style={itemStyle(index)}
            >
              <div className="mb-2">{line(index % 2 === 0 ? '5rem' : '7rem', 'h-2.5')}</div>
              <Skeleton variant="rectangular" className="h-11 w-full" />
            </div>
          ))}
          {withFormAction && (
            <div
              data-skeleton-item="action"
              data-skeleton-motion="item"
              className={`skeleton-item-enter motion-reduce:animate-none ${formLayout === 'responsive' ? 'sm:col-span-2' : ''}`}
              style={itemStyle(lines)}
            >
              <Skeleton variant="rectangular" className="h-11 w-full" />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {Array.from({ length: lines }, (_, index) => (
            <React.Fragment key={index}>{line(index === lines - 1 ? '62%' : index % 2 === 0 ? '96%' : '84%')}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export const PageSkeleton: React.FC<PageSkeletonProps> = ({
  label,
  variant,
  itemCount,
  formLayout = 'responsive',
  withFormAction = false,
  className = '',
}) => {
  const count = itemCount ?? (variant === 'dashboard' ? 4 : 5);

  return (
    <PageSkeletonFrame label={label} className={className}>
      <div data-skeleton-variant={variant} className="space-y-6 sm:space-y-8">
        <SkeletonStage index={0}>
          <PageHeaderSkeleton withActions={variant !== 'form'} />
        </SkeletonStage>

        {variant === 'list' && (
          <>
            <SkeletonStage index={1}><SummaryStripSkeleton count={3} /></SkeletonStage>
            <SkeletonStage index={2}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Skeleton variant="rectangular" className="h-10 w-full sm:max-w-sm" />
                <Skeleton variant="rectangular" className="h-9 w-28" />
              </div>
            </SkeletonStage>
            <SkeletonStage index={3}><ListSkeleton count={count} /></SkeletonStage>
          </>
        )}

        {variant === 'detail' && (
          <>
            <SkeletonStage index={1}><SummaryStripSkeleton count={3} /></SkeletonStage>
            <SkeletonStage index={2}>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                <ContentBlockSkeleton className="xl:col-span-8" lines={5} />
                <ContentBlockSkeleton className="xl:col-span-4" lines={3} />
              </div>
            </SkeletonStage>
          </>
        )}

        {variant === 'dashboard' && (
          <>
            <SkeletonStage index={1}><SummaryStripSkeleton count={4} /></SkeletonStage>
            <SkeletonStage index={2}><ListSkeleton count={count} presentation="cards" /></SkeletonStage>
            <SkeletonStage index={3}><ListSkeleton count={3} /></SkeletonStage>
          </>
        )}

        {variant === 'form' && (
          <SkeletonStage index={1} className="mx-auto w-full max-w-3xl">
            <ContentBlockSkeleton
              variant="form"
              lines={count}
              formLayout={formLayout}
              withFormAction={withFormAction}
            />
          </SkeletonStage>
        )}
      </div>
    </PageSkeletonFrame>
  );
};
